import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { areRelatedServicesLoading } from 'features/related-services';
import { getArticle as getAfterLocationUpdateId, getRobotintConfig } from 'common/selectors';
import { getCartItem } from 'features/basket';
import {
  getActiveTintColor,
  getGtinGtin13Master,
  getIsTintServiceAvailable,
  getProductId,
  getSelectedTintColors,
} from 'features/product-info/ducks/selectors';
import { RootReducer } from 'store/root-reducer';
import {
  SET_ACTIVE_TINT_COLOR,
  SET_IS_TINT_SERVICE_AVAILABLE,
  ADD_COLOR_TO_SELECTED_COLORS,
  DELETE_COLOR_FROM_COOKIE,
  SELECT_COLOR_BUTTON_IS_IN_VIEWPORT,
  SELECT_COLOR_BUTTON_IS_CLICKED,
} from 'features/product-info/ducks/product-tint-service';
import { collectionNameContainer, selectTintColorContainer } from './styles';
import { TRobotintConfig, TTintColor, TTintColorWithQuantity } from './types';
import { useRobotintScript } from './hooks/use-robotint-script.hook';
import { ChooseColorButton } from './components/choose-color-button';
import { ColorPalette } from './components/color-palette/color-palette';
import { MAX_PRODUCT_QUANTITY_IN_BASKET } from './pdp-select-tint-color.const';
import { useTintColor } from './hooks/use-tint-color';
import { TintLabel } from './components/tint-label/tint-label';
import { useTintColorUpdated } from './hooks/use-tint-color-updated';
import { useTintServiceQuantityForSelectedColorUpdated } from './hooks/use-tint-service-quantity-for-selected-color-updated';

type TProps = {
  gtin_GTIN13_master?: string;
  robotintConfig: TRobotintConfig;
  productQuantityInBasket: number;
  setIsTintServiceAvailable: (isAvailable: boolean) => void;
  isTintServiceAvailable: boolean;
  setActiveColor: (activeColor: null | TTintColorWithQuantity) => void;
  setSelectedTintColors: (selectedColor: TTintColor | never) => void;
  selectedTintColors: never | TTintColorWithQuantity[];
  activeTintColor: null | TTintColor;
  deleteColorFromSelected: () => void;
  productIdAfterVariantChange: string;
  sendGaImpressions: () => void;
  sendGaClickSelectColorButton: () => void;
  isTintServicePriceLoading: boolean;
};

export const SelectTintColor = ({
  gtin_GTIN13_master,
  robotintConfig,
  productQuantityInBasket,
  setIsTintServiceAvailable,
  isTintServiceAvailable,
  setActiveColor,
  setSelectedTintColors,
  activeTintColor,
  selectedTintColors,
  deleteColorFromSelected,
  productIdAfterVariantChange,
  sendGaImpressions,
  sendGaClickSelectColorButton,
  isTintServicePriceLoading,
}: TProps) => {
  const isAnyColorsSelected = selectedTintColors.length > 0;

  const {
    handleClickTransparentColorButton,
    handleSelectColor,
    handleChangeActiveColor,
    openChooseColorModal,
    handleDeleteColor,
  } = useTintColor({
    setSelectedTintColors,
    deleteColorFromSelected,
    setActiveColor,
    sendGaClickSelectColorButton,
    isTintServiceAvailable,
    setIsTintServiceAvailable,
  });

  useRobotintScript({
    robotintConfig,
    handleSelectColor,
    productIdAfterVariantChange,
  });

  useTintColorUpdated({
    handleSelectColor,
    handleDeleteColor,
    isTintServiceAvailable,
    setIsTintServiceAvailable,
  });
  useTintServiceQuantityForSelectedColorUpdated();

  if (!gtin_GTIN13_master || robotintConfig.robotintLink.length === 0) {
    return null;
  }

  const dataForIntegration = JSON.stringify({
    BARCODE: gtin_GTIN13_master,
  });

  return (
    <div data-qa="select-tint-color-container-pdp" className={selectTintColorContainer}>
      {!isAnyColorsSelected && (
        <ChooseColorButton
          sendGaImpressions={sendGaImpressions}
          isTintAvailable={isTintServiceAvailable}
          openChooseColorModal={openChooseColorModal}
          dataForIntegration={dataForIntegration}
        />
      )}
      <div>
        <div className={collectionNameContainer}>
          <TintLabel
            isTintServicePriceLoading={isTintServicePriceLoading}
            activeTintColor={activeTintColor}
            handleDeleteColor={handleDeleteColor}
            countOfSelectedColors={selectedTintColors.length}
          />
        </div>

        {isAnyColorsSelected && (
          <ColorPalette
            isTintServiceAvailable={isTintServiceAvailable}
            handleClickTransparentColorButton={handleClickTransparentColorButton}
            handleChangeActiveColor={handleChangeActiveColor}
            openChooseColorModal={openChooseColorModal}
            selectedTintColors={selectedTintColors}
            activeColor={activeTintColor}
            isShowAddNewColorButton={productQuantityInBasket < MAX_PRODUCT_QUANTITY_IN_BASKET}
            dataForIntegration={dataForIntegration}
          />
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setIsTintServiceAvailable: (isAvailable: boolean) => dispatch(SET_IS_TINT_SERVICE_AVAILABLE(isAvailable)),
  setActiveColor: (activeColor: null | TTintColorWithQuantity) =>
    dispatch(SET_ACTIVE_TINT_COLOR(activeColor)),
  setSelectedTintColors: (newSelectedColor: TTintColor) =>
    dispatch(ADD_COLOR_TO_SELECTED_COLORS(newSelectedColor)),
  deleteColorFromSelected: () => dispatch(DELETE_COLOR_FROM_COOKIE()),
  sendGaImpressions: () => dispatch(SELECT_COLOR_BUTTON_IS_IN_VIEWPORT()),
  sendGaClickSelectColorButton: () => dispatch(SELECT_COLOR_BUTTON_IS_CLICKED()),
});

const mapStoreToProps = (store: RootReducer) => {
  const productId = getProductId(store) || '';
  const productQuantityInBasket = getCartItem(productId)(store)?.quantity || 0;
  const gtinGtin13Master = getGtinGtin13Master(store);
  const robotintConfig = getRobotintConfig(store);

  const isTintServiceAvailable = getIsTintServiceAvailable(store);
  const activeTintColor = getActiveTintColor(store);
  const selectedTintColors = getSelectedTintColors(store);

  const productIdAfterVariantChange = getAfterLocationUpdateId(store);

  const isTintServicePriceLoading = areRelatedServicesLoading(store);

  return {
    gtin_GTIN13_master: gtinGtin13Master,
    robotintConfig,
    productQuantityInBasket,
    isTintServiceAvailable,
    activeTintColor,
    selectedTintColors,
    productIdAfterVariantChange,
    isTintServicePriceLoading,
  };
};

export const PdpSelectTintColor = connect(mapStoreToProps, mapDispatchToProps)(SelectTintColor);
